<template>
  <div>
    <div
      v-for="alert in visibleAlerts"
      :key="alert.code + ':' + alert.scope + ':' + (alert.subject || '')"
      :class="['platform-alert-banner', `platform-alert-banner--${severityClass(alert)}`]"
    >
      <div class="platform-alert-banner__inner">
        <img
          class="platform-alert-banner__icon"
          src="/img/renova-dashboard/icons/error.svg"
          alt="warning"
        />
        <p class="platform-alert-banner__text body-text">
          {{ alertText(alert) }}
        </p>
        <BaseButton
          v-if="isUserAdmin || alert.scope === 'PERSONAL'"
          class="platform-alert-banner__cta"
          :is-small="true"
          @click="goTo(alert)"
        >
          {{ ctaLabel(alert) }} →
        </BaseButton>
        <button
          type="button"
          class="platform-alert-banner__close"
          :aria-label="$t('buttons.close')"
          :title="$t('buttons.close')"
          @click="dismiss(alert)"
        >
          ×
        </button>
      </div>
    </div>
    <div v-if="hiddenCount > 0" class="platform-alert-banner platform-alert-banner--warning">
      <div class="platform-alert-banner__inner">
        <p class="platform-alert-banner__text body-text">
          <a class="platform-alert-banner__more" @click="goToIntegrations">
            {{ $t('platformAlerts.more', { count: hiddenCount }) }}
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { USER_ROLES } from '@/store/modules/user';
import { platformAlertKey } from '@/utils/platformAlertKey';
import {
  alertDismissalKey,
  dismissalStorageKey,
  loadDismissed,
  pruneDismissed,
  saveDismissed,
} from '@/utils/platformAlertDismissal';
import { reconnectModalFor } from '@/utils/platformAlertReconnect';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';

/**
 * The platform-wide alert strips.
 *
 * The backend has already decided which of these this user may see — a colleague's personal
 * WhatsApp seat never reaches the payload — so this component does no filtering of its own. It
 * only decides how many to show at once, what to say, and which strips this user has closed.
 *
 * Closing is per user and per workspace, in localStorage: a COMPANY alert closed by one person
 * stays up for their colleagues.
 */
const MAX_VISIBLE = 3;

export default {
  name: 'PlatformAlertBanner',
  components: { BaseButton },
  data() {
    return { dismissed: [] };
  },
  computed: {
    ...mapGetters(['platformAlerts', 'platformAlertsLoaded', 'userRole', 'user']),
    // Same source the layout uses to pick the workspace; switching workspace reloads the page.
    storageKey() {
      const userId = this.user && this.user.id;
      if (!userId) return null;
      let providerId = null;
      try {
        providerId = localStorage.getItem(`${userId}_insuranceProvider`);
      } catch (e) {
        providerId = null;
      }
      return dismissalStorageKey(userId, providerId);
    },
    isUserAdmin() {
      return this.userRole === USER_ROLES.ADMIN;
    },
    /**
     * Alerts we have copy for. An unknown code from a newer backend renders nothing rather than a
     * raw translation key: a deploy skew should look like a missing banner, not like a bug.
     */
    knownAlerts() {
      return (this.platformAlerts || []).filter((alert) => alert
        && this.$te(this.textKey(alert))
        && !this.dismissed.includes(alertDismissalKey(alert)));
    },
    // Three strips already push the page down ~150px; beyond that the banner is the page.
    visibleAlerts() {
      return this.knownAlerts.slice(0, MAX_VISIBLE);
    },
    hiddenCount() {
      return Math.max(0, this.knownAlerts.length - MAX_VISIBLE);
    },
  },
  watch: {
    storageKey: {
      immediate: true,
      handler(key) {
        this.dismissed = loadDismissed(key);
        this.prune();
      },
    },
    platformAlerts() {
      this.prune();
    },
  },
  methods: {
    dismiss(alert) {
      const key = alertDismissalKey(alert);
      if (this.dismissed.includes(key)) return;
      this.dismissed = [...this.dismissed, key];
      saveDismissed(this.storageKey, this.dismissed);
    },
    // Forget closed strips whose problem is gone, so the next lapse shows again. Only once the
    // list has really loaded — before the first fetch it is empty and would wipe everything.
    prune() {
      if (!this.platformAlertsLoaded || !this.dismissed.length) return;
      const kept = pruneDismissed(this.dismissed, this.platformAlerts);
      if (kept.length === this.dismissed.length) return;
      this.dismissed = kept;
      saveDismissed(this.storageKey, kept);
    },
    textKey(alert) {
      return platformAlertKey(alert);
    },
    /**
     * Defaults to the softer tone rather than trusting the field to be there. A payload from an
     * older or partial backend that omits `severity` would otherwise throw inside the class
     * binding and take the whole banner list down with it — losing the alerts that ARE well-formed
     * to protect nothing.
     */
    severityClass(alert) {
      return alert.severity === 'ERROR' ? 'error' : 'warning';
    },
    alertText(alert) {
      // Product names are already translated under `subscription.*`; interpolating one string
      // rather than enumerating one per product keeps six strings per status out of the locales.
      const product = alert.subject && this.$te(`subscription.${alert.subject}`)
        ? this.$t(`subscription.${alert.subject}`)
        : alert.subject;
      return this.$t(this.textKey(alert), { product, phone: alert.phone || alert.subject });
    },
    /**
     * CTA labels reuse existing keys on purpose. i18n-check ratchets divergent translations of
     * the same short English string, and "Pay" / "Reconnect" already exist — adding second copies
     * would fail CI for no benefit.
     */
    ctaLabel(alert) {
      if (alert.code === 'WHATSAPP_DISCONNECTED') {
        return this.$t('Integrations.whatsapp.Reconnect');
      }
      return this.$t('Integrations.whatsapp.PaySubscription');
    },
    goTo(alert) {
      const target = alert.ctaTarget || '/admin/integrations';
      const reconnect = reconnectModalFor(alert);
      if (reconnect) {
        // Pushed even when already on Integrations: the page watches the query and opens the modal.
        // The catch swallows NavigationDuplicated from a double click before the page clears it.
        this.$router.push({ path: target, query: { reconnect } }).catch(() => {});
        return;
      }
      if (this.$route.path !== target) this.$router.push(target);
    },
    goToIntegrations() {
      if (this.$route.path !== '/admin/integrations') this.$router.push('/admin/integrations');
    },
  },
};
</script>

<style lang="scss" scoped>
// No positioning here: PlatformBanners owns the fixed strip so several banners can stack.
.platform-alert-banner {
  padding: 10px 24px;
}

.platform-alert-banner--warning {
  background: #FFF4CC;
  border-bottom: 1px solid #E6D48F;

  .platform-alert-banner__text {
    color: #4A3B00;
  }
}

.platform-alert-banner--error {
  background: #FFE9E9;
  border-bottom: 1px solid #F0BABA;

  .platform-alert-banner__text {
    color: #8A1F1F;
  }
}

.platform-alert-banner__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  max-width: 100%;
}

.platform-alert-banner__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.platform-alert-banner__text {
  margin: 0;
  text-align: center;
}

.platform-alert-banner__more {
  cursor: pointer;
  text-decoration: underline;
}

.platform-alert-banner__cta {
  flex-shrink: 0;
  margin-left: 16px;
}

.platform-alert-banner__close {
  flex-shrink: 0;
  padding: 0 4px;
  border: none;
  background: none;
  color: inherit;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
}

.platform-alert-banner--warning .platform-alert-banner__close {
  color: #4A3B00;
}

.platform-alert-banner--error .platform-alert-banner__close {
  color: #8A1F1F;
}
</style>
